import React, { useState } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Small fixed pill in the top-left that returns admin users to /admin.
 * Renders only when:
 *   - user is logged in AND is an admin / main_admin
 *   - current path is NOT already inside /admin
 *   - current path is NOT an auth / marketing route
 *
 * HISTORICAL BUG (fixed 2026-07-21):
 * ---------------------------------
 * The pill previously called `navigate('/admin/dashboard')` directly. But
 * the `/admin/*` routes are wrapped in `<ProtectedRoute requiredRole="admin">`,
 * and the user's ACTIVE session role (in the JWT) is whatever role they
 * most recently switched into — e.g. `venue_owner`, `host`, `guest`,
 * `venue_member`. So the ProtectedRoute rejected the pill navigation
 * with a redirect to /login → the user saw a bounce, or worse, ended up
 * on a page that appeared blank. Reported by the user as: "the pill
 * only worked 2-3 times in the last month".
 *
 * The correct flow (mirrors SwitchRolePage's handleRoleTap('admin')):
 *   1. Call POST /api/auth/switch-role with target_role='admin'
 *   2. Update the JWT + user object in localStorage from the response
 *   3. Do a full window.location.href navigation to '/admin' so the
 *      whole app tree remounts with the fresh admin session (not just
 *      a client-side route change)
 *
 * For promoted admins who haven't unlocked yet (`is_admin_unlocked`
 * false + `is_main_admin` false), we do NOT show the pill — those
 * users must enter their 4-digit unlock code via SwitchRolePage first.
 */
const HIDDEN_PREFIXES = [
  '/admin',
  '/login',
  '/register',
  '/welcome',
  '/about',
  '/contact',
  '/privacy',
  '/terms',
  '/delete-account',
  '/account-deleted',
  '/select-role',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminReturnPill({ user }) {
  const location = useLocation();
  const [switching, setSwitching] = useState(false);

  if (!user) return null;
  if (!user.is_admin && !user.is_main_admin) return null;
  // Promoted admins who haven't unlocked yet — don't offer the shortcut.
  if (!user.is_main_admin && !user.is_admin_unlocked) return null;
  const path = location.pathname || '';
  if (HIDDEN_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return null;
  }

  const handleClick = async () => {
    if (switching) return;

    // Already in admin role? Just navigate — no round-trip needed.
    if (user.role === 'admin') {
      window.location.href = '/admin';
      return;
    }

    setSwitching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/auth/switch-role`,
        { target_role: 'admin' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Persist the fresh JWT + user object so the next route match sees
      // role='admin' and passes the ProtectedRoute guard.
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // Full navigation so the whole App tree remounts with the new session,
      // matching the pattern used by SwitchRolePage.switchDirectly().
      window.location.href = '/admin';
    } catch (err) {
      setSwitching(false);
      toast.error(err?.response?.data?.detail || 'Could not switch to admin — please try Switch to.. from your profile.');
    }
  };

  return (
    <button
      type="button"
      data-testid="admin-return-pill"
      onClick={handleClick}
      disabled={switching}
      title="Back to admin panel"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 72px)',
        left: 12,
        zIndex: 60,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 12px',
        borderRadius: 9999,
        border: '1px solid var(--card-border)',
        background: 'var(--nav-surface)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        color: '#C8102E',
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        boxShadow: 'var(--nav-shadow)',
        cursor: switching ? 'wait' : 'pointer',
        opacity: switching ? 0.7 : 1,
      }}
    >
      {switching ? (
        <Loader2 size={13} className="animate-spin" />
      ) : (
        <Shield size={13} />
      )}
      {switching ? 'Switching…' : 'Admin'}
    </button>
  );
}
